import { motion } from "framer-motion";
import { FAMILIES } from "@/lib/wedding";

/**
 * Families
 * -----------------------------------------------------------
 * "Welcoming Families": gold-bordered cards (hosts, compliments, sharing
 * happiness, well wishers) and one tap-to-call contact line under them.
 */
const CARDS = [
  { label: "Swagatotsuk", sub: "Welcomed with joy by", lines: FAMILIES.swagatotsuk },
  // own brothers right after the hosts, then everyone else's compliments
  { label: "Sharing Happiness", sub: "Joyfully shared by", lines: FAMILIES.sharingHappiness },
  { label: "Best Compliments", sub: "With best compliments from", lines: FAMILIES.compliments },
  { label: "Best Compliments", sub: "Maternal side", lines: FAMILIES.complimentsMaternal },
  { label: "Best Compliments", sub: "Paternal side", lines: FAMILIES.complimentsPaternal },
  { label: "Best Compliments", sub: "Siblings", lines: FAMILIES.siblings },
  { label: "Darshanabhilashi", sub: "Awaiting your blessings", lines: FAMILIES.darshanabhilashi },
].filter((c) => c.lines?.length > 0); // a card with no names is hidden

export default function Families() {
  return (
    <section id="families" className="relative edge py-24 text-center" style={{ background: "linear-gradient(180deg, #FAF6EE 0%, #F1E9D8 14%, #F1E9D8 86%, #FAF6EE 100%)" }}>
      <div className="overline text-rani mb-5">Mangal Aashirwad</div>
      <h2 className="font-display text-4xl sm:text-6xl text-rani">Welcoming Families</h2>
      <div
        className="mx-auto mt-6 mb-6 h-px w-40"
        style={{ background: "linear-gradient(90deg, rgba(201,169,97,0) 0%, rgba(201,169,97,0.7) 50%, rgba(201,169,97,0) 100%)" }}
      />
      <p className="mb-12 font-display italic text-xl text-ink/70 max-w-2xl mx-auto">
        A warm invitation from our families to join us in celebrating this divine union.
      </p>

      <div className={`mx-auto grid gap-6 ${CARDS.length > 1 ? "max-w-4xl md:grid-cols-2" : "max-w-md"}`}>
        {CARDS.map((c, i) => (
          <motion.div
            key={c.label + c.sub}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="border border-gold bg-ivory rounded-2xl px-6 py-10"
          >
            <div className="font-display italic text-rani text-2xl">{c.label}</div>
            <div className="mt-1 mb-5 text-[11px] uppercase tracking-chip text-gold-deep">{c.sub}</div>
            {c.lines.map((l) => {
              // a line is either plain text or { name, role }
              const name = typeof l === "string" ? l : l.name;
              const role = typeof l === "string" ? null : l.role;
              return (
                <div key={name} className="mt-3">
                  {/* "Mrs. X & Mr. Y" (the parents) reads best as two lines, like the Alliance */}
                  <p className="font-display text-lg text-ink leading-snug">
                    {name.includes(" & Mr. ") ? (
                      <>
                        {name.split(" & Mr. ")[0]}
                        <br />& Mr. {name.split(" & Mr. ")[1]}
                      </>
                    ) : (
                      name
                    )}
                  </p>
                  {role && <p className="text-[10px] uppercase tracking-chip text-gold-deep mt-0.5">{role}</p>}
                </div>
              );
            })}
          </motion.div>
        ))}
      </div>

      {FAMILIES.contact?.length > 0 && (
        // phones: label on top, numbers stacked and centred; sm+: one dotted row
        <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-1.5 sm:gap-0 font-body text-sm text-ink/80 leading-loose">
          <span className="sm:mr-2 mb-1 sm:mb-0 text-[10px] uppercase tracking-chip text-rani font-semibold">Contact</span>
          {FAMILIES.contact.map((p, i) => (
            <span key={p} className="whitespace-nowrap">
              {i > 0 && <span className="hidden sm:inline mx-2 text-gold">·</span>}
              <a href={`tel:${p.replace(/[^+\d]/g, "")}`} className="text-gold-deep hover:text-rani transition-colors tabular-nums">
                {p}
              </a>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
